import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = guest, object = user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("lp_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) {
      localStorage.removeItem("lp_token");
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  /**
   * Login. On success returns {ok:true}. If the account exists but is not
   * verified, the backend returns 403 with {code:"email_not_verified", email}.
   * We pass that through so the UI can offer a "resend" path.
   */
  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("lp_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        return {
          ok: false,
          unverified: true,
          email: detail.email || email,
          language: detail.language || null,
        };
      }
      return { ok: false, error: formatApiError(detail) || e.message };
    }
  };

  /**
   * Register no longer signs the user in. Instead it returns the email so the
   * UI can show the "check inbox" confirmation screen.
   */
  const register = async (payload) => {
    try {
      const body = { ...payload, origin: window.location.origin };
      const { data } = await api.post("/auth/register", body);
      return { ok: true, email: data.user.email, requiresVerification: data.requires_verification };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  /** Verify-email exchanges a verification token for an auth token. */
  const verifyEmail = async (token) => {
    try {
      const { data } = await api.post("/auth/verify-email", { token });
      localStorage.setItem("lp_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code =
        detail && typeof detail === "object" ? detail.code : "invalid_token";
      return { ok: false, code };
    }
  };

  const resendVerification = async (email) => {
    try {
      await api.post("/auth/resend-verification", { email, origin: window.location.origin });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = () => {
    localStorage.removeItem("lp_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, login, register, verifyEmail, resendVerification, logout, refresh: fetchMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
